import { PayloadAction } from "@reduxjs/toolkit";
import { createApi, createSlice } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { InsightOverviewState } from "./types";
import { baseQuery, endpoints, formatErrors } from 'utils/api/endpoints';

export const initialState: InsightOverviewState = {
  loading: false,
  error: null,
  insightOverview: null,
};

// RTK Query API for insight overview
export const api = createApi({
  reducerPath: 'insightOverviewApi',
  tagTypes: ['InsightOverview', 'GoogleReviews'],
  baseQuery,
  endpoints: builder => ({
    // Get insight overview data
    getInsightOverview: builder.query<any, void>({
      query: (params: any) => ({
        url: endpoints.insightOverview.url,
        method: endpoints.insightOverview.method,
        params,
      }),
      transformResponse: (response: any) => response || null,
      providesTags: ['InsightOverview'],
    }),
    // paid campaigns
    getPaidCampaigns: builder.query<any, { status?: any }>({
      query: (params: any) => ({
        url:
          params.status !== undefined && params.status !== null
            ? `${endpoints.paidCampaigns.url}?status=${params.status}`
            : endpoints.paidCampaigns.url,
        method: endpoints.paidCampaigns.method,
      }),
    }),
    // new patients charts
    getNewPatientsCharts: builder.query<any, { filterType: string, year: number, month?: string, type: number }>({
      query: ({ filterType, year, month, type }) => {
        const params: any = {
          filterType,
          year,
          type,
        };
        // Only include month when filterType is 'custom'
        if (filterType === 'custom' && month) {
          params.month = month;
        }
        return {
          url: endpoints.newPatientsCharts.url,
          method: endpoints.newPatientsCharts.method,
          params,
        };
      },
    }),
    // website health checkup
    getWebsiteTraffic: builder.query<any, void>({
      query: (params: any) => ({
        url: endpoints.websiteHealthCheckup.url,
        method: endpoints.websiteHealthCheckup.method,
        params,
      }),
    }),
    // google rankings by keywords
    getGoogleRankingsByKeywords: builder.query<any, { sortOrder?: string , sortBy?: string, page?: number, limit?: number}>({
      query: (params: any) => {
        const baseUrl = endpoints.googleRankingsByKeywords.url;
        const method = endpoints.googleRankingsByKeywords.method;
        const searchParams = new URLSearchParams();
        if (params && params.sortOrder != null) {
          searchParams.append('sortOrder', params.sortOrder);
        }
        if (params && params.sortBy != null) {
          searchParams.append('sortBy', params.sortBy);
        }
        if (params && params.page != null) {  
          searchParams.append('page', params.page.toString());
        }
        if (params && params.limit != null) {
          searchParams.append('limit', params.limit.toString());
        }
        const url = searchParams.toString() ? `${baseUrl}?${searchParams.toString()}` : baseUrl;
        return {
          url,
          method,
        };
      },
    }),
    // keyword opportunities
    getKeywordOpportunities: builder.query<any, { sortOrder?: string, page?: number, limit?: number }>({
      query: (params: any) => {
        const baseUrl = endpoints.keywordOpportunities.url;
        const method = endpoints.keywordOpportunities.method;
        const searchParams = new URLSearchParams();
        if (params && params.sortOrder) {
          searchParams.append('sortOrder', params.sortOrder);
        }
        if (params && params.page != null) {
          searchParams.append('page', params.page.toString());
        }
        if (params && params.limit != null) {
          searchParams.append('limit', params.limit.toString());
        }
        const url = searchParams.toString() ? `${baseUrl}?${searchParams.toString()}` : baseUrl;
        return {
          url,
          method,
        };
      },
    }),
    // google reviews
    getGoogleReviews: builder.query<any, void>({
      query: () => ({
        url: endpoints.googleReviews.url,
        method: endpoints.googleReviews.method,
      }),
      transformResponse: (response: any) => response || null,
      transformErrorResponse: formatErrors, 
    }),
    // get google review unreplied
    getGoogleReviewUnreplied: builder.query<any, void>({
      query: () => ({
        url: endpoints.googleReviewUnreplied.url,
        method: endpoints.googleReviewUnreplied.method,
      }),
      providesTags: ['GoogleReviews'],
      transformResponse: (response: any) => response || null,
      transformErrorResponse: formatErrors, 
    }),
    // write a review
    getWriteAReview: builder.mutation<any, any>({
      query: (body: any) => ({
        url: endpoints.googleReviewReply.url,
        method: endpoints.googleReviewReply.method,
        body,
      }),
      invalidatesTags: ['GoogleReviews'],
      transformResponse: (response: any) => response || null,
      transformErrorResponse: formatErrors, 
    }),
    // how people find us
    howPeopleFindUs: builder.query<any, void>({
      query: () => ({
        url: endpoints.howPeopleFindUs.url,
        method: endpoints.howPeopleFindUs.method,
      }),
      providesTags: ['GoogleReviews'],
      transformResponse: (response: any) => response || null,
      transformErrorResponse: formatErrors, 
    }),
  }),
});

const slice = createSlice({
  name: "insightOverview",
  initialState,
  reducers: {
    someAction(state, action: PayloadAction<any>) {},
  },
});

export const { actions: insightOverviewActions, reducer: insightOverviewReducer } = slice;

export const useInsightOverviewSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });

  // RTK Query injects itself
  return {
    actions: slice.actions,
    ...api,
  };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
